import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { flushSync } from 'react-dom';

import { reportFeedFreshness } from '../../lib/connectionFreshnessBootstrap';
import { getMetaContent, isFeatureFlagEnabled } from '../../lib/meta';
import { consumeMessageJump, MESSAGE_JUMP_REQUESTED_EVENT } from '../../lib/pendingMessageJump';
import { channelIdFromEditorId, findRecentEditableOwnMessage, isEditableOwnMessageRow } from '../../lib/recentEditableMessage';
import { useUnreadCounts } from '../../lib/unread_store';

import { useWindowEvent } from '../../hooks/useWindowEvent';

import { KEYCHAIN, useAnyKeychainsRegistered } from '../../contexts/KeyMaster';
import { useRichdownComposerFocusRouter } from '../../contexts/RichdownComposerFocusRouter';
import ConferenceTranscriptModal from '../../components/modals/ConferenceTranscriptModal';
import DeleteMessageConfirmation from '../../components/modals/DeleteMessageConfirmation';
import FullMessageModal from '../../components/modals/FullMessageModal';
import ImageRemixModal from '../../components/modals/ImageRemixModal';
import { ModalPortal, useRetainedModalValue } from '../../components/modals/ModalPortal';
import { LIVE_MESSAGE_EVENT } from '../../components/chat/events';
import MessagesFooter from '../../components/MessagesFooter';
import NetworkStatus from '../../components/NetworkStatus';
import ImageAttachmentLightbox from '../../components/messages/ImageAttachmentLightbox';
import JumpToNewest from '../../components/messages/JumpToNewest';
import MessageListItem from '../../components/messages/MessageListItem';
import { shouldFetchLatestForJump, type FeedController, type FeedControllerSnapshot } from '../core/controller';
import { isRecord } from '../core/record';
import {
  IDLE_BOOT_LATEST_VALIDATION_STATE,
  IDLE_RETURN_TO_PRESENT_STATE,
  type FeedMessageJumpInput,
  type FeedSession,
  type FeedSessionSnapshot
} from '../core/session';
import type { ChatItem, MessageId, SortOrder } from '../core/types';
import { FeedReadCursorController, type ReadCursorContext } from '../echo/readCursor';
import { EchoReplyResolver, type ReplyParentRow } from '../echo/replyResolution';
import { isInvalidEchoMessage } from '../echo/invalidMessage';
import {
  type FeedEngine,
  type RenderWindow,
  type ScrollState,
  type ViewportInsets
} from '../engine';
import { BootSkeleton, FeedPlaceholderRow } from './BootSkeleton';
import { FeedEdgeZone } from './FeedEdgeZones';
import { FeedErrorBoundary } from './FeedErrorBoundary';
import { FeedScrollbar } from './FeedScrollbar';
import { InvalidMessageRow } from './InvalidMessageRow';
import type { EntitySnapshotMaps } from './entityBundle';
import { markFeedJumpComplete } from './feedPerformance';
import { handleFeedMessageJumpRequest } from './messageJump';
import { buildMessageFeedItemContext } from './messageFeedContext';
import { useMessageTargets } from './messageTargets';
import {
  areRendererRowsDifferentDays,
  createRendererRowCache,
  type MessageListRendererRow
} from './rendererRows';
import { unreadDividerSortOrderForLoadedItems } from './unreadDivider';
import { useFeedSessionLifecycle } from './useFeedSessionLifecycle';

const ENGINE_SCROLLER_ID = 'message-feed-scroller';
const COMPOSER_DEFAULT_PX = 100;
const EMPTY_CURRENT_CHANNEL_BREADCRUMBS: readonly unknown[] = [];
const JUMP_TO_NEWEST_THRESHOLD = 200;
// Reading gap between the newest message and the composer when pinned at bottom; part of
// the overlay inset so AT_BOTTOM geometry owns it (never also add padding — Spec P9).
const COMPOSER_GAP_PX = 24;
const JUMP_HIGHLIGHT_DURATION_MS = 1500;
const FEED_SCROLL_LOCK_KEYCHAINS = [
  KEYCHAIN.POPOVER__CONTEXT_MENU,
  KEYCHAIN.POPOVER__SIDEBAR_CONTEXT_MENU,
  KEYCHAIN.POPOVER__SIDEBAR_SECTION_CONTEXT_MENU,
  KEYCHAIN.POPOVER__MESSAGE_REACTION_EMOJIBAR,
  KEYCHAIN.POPOVER__MESSAGE_REACTION_CONTEXT_MENU_EMOJIBAR
];
const SCROLLER_STYLE: React.CSSProperties = {
  overflowAnchor: 'none',
  overscrollBehavior: 'contain',
  scrollbarGutter: 'stable',
  transform: 'translateZ(0)'
};
const ITEM_SHELL_STYLE: React.CSSProperties = {
  contain: 'layout'
};
const EMPTY_SESSION_SNAPSHOT: FeedSessionSnapshot = {
  bootLatestValidationState: IDLE_BOOT_LATEST_VALIDATION_STATE,
  bufferHealthy: false,
  conversationId: '',
  returnToPresentState: IDLE_RETURN_TO_PRESENT_STATE,
  status: 'Feed session unavailable',
  storageWriteFailureEnabled: false,
  unseenPresentCount: 0
};
const itemShellRenderCounts = new Map<string, number>();
const messageListItemCommitCounts = new Map<string, number>();
const rendererRowBuildCounts = new Map<string, number>();
let rendererRowCacheCreationCount = 0;
const EMPTY_LOADED_ITEM_SNAPSHOT: LoadedItemSnapshot = {
  epoch: -1,
  indexesByKey: new Map(),
  items: []
};

interface MessageFeedProps {
  readonly bottomOverlayHeight?: number;
  readonly currentChannelBreadcrumbs?: readonly unknown[];
  readonly currentChannel?: {
    readonly id?: string;
    readonly kind?: string;
    readonly oldest_message_sort_order?: string;
    readonly workspace_id?: string;
  } | null;
  readonly dropzoneHelpers?: unknown;
  readonly handleDropzoneStateChange?: (state: unknown) => void;
  readonly handleUploadCountChange?: (count: unknown) => void;
  readonly isSharedChannel?: boolean;
  readonly maxMediaAttachments?: number;
  readonly maxRichdownContentSize?: number;
}

type LegacyComponentProps = Record<string, unknown> & {
  readonly children?: React.ReactNode;
};

interface MessageFeedDebugSurface {
  /** Read-only E2E/debug contract: small engine state sample for layout assertions. */
  readonly engineState: () => {
    readonly atBottom: boolean;
    readonly edges: ReturnType<FeedController['snapshot']>['edgeState'];
    readonly epoch: number;
    readonly loadedCount: number;
    readonly mode: ScrollState['mode'];
  };
  /** Read-only E2E/debug contract: cursor/count snapshot, without controller mutation access. */
  readonly controllerSnapshot: () => {
    readonly edges: ReturnType<FeedController['snapshot']>['edgeState'];
    readonly itemCount: number;
    readonly loading: ReturnType<FeedController['snapshot']>['loading'];
    readonly newerCursor: string | null;
    readonly olderCursor: string | null;
    readonly syncCursor: string | null;
    readonly windowEpoch: number;
  };
  /** Read-only E2E/debug contract: ItemShell render count by item key since feed mount. */
  readonly renderCounts: () => Record<string, number>;
  /** Read-only E2E/debug contract: work performed below ItemShell by item key. */
  readonly workCounts: () => MessageFeedWorkCounts;
}

interface MessageFeedWorkCounts {
  readonly messageListItemCommits: Record<string, number>;
  readonly rendererRowBuilds: Record<string, number>;
  readonly rendererRowCacheCreations: number;
}

interface LoadedItemSnapshot {
  readonly epoch: number;
  readonly indexesByKey: ReadonlyMap<string, number>;
  readonly items: readonly ChatItem[];
}

declare global {
  interface Window {
    __messageFeed?: MessageFeedDebugSurface;
  }
}

// SAFETY: These bridge legacy JS components that do not yet publish TypeScript props.
// The components keep their runtime prop validation/handling; remove this group once
// those files gain typed exports.
const LegacyConferenceTranscriptModal = ConferenceTranscriptModal as unknown as React.ComponentType<LegacyComponentProps>;
const LegacyDeleteMessageConfirmation = DeleteMessageConfirmation as unknown as React.ComponentType<LegacyComponentProps>;
const LegacyFullMessageModal = FullMessageModal as unknown as React.ComponentType<LegacyComponentProps>;
const LegacyImageAttachmentLightbox = ImageAttachmentLightbox as unknown as React.ComponentType<LegacyComponentProps>;
const LegacyMessageListItem = MessageListItem as unknown as React.ComponentType<LegacyComponentProps>;
const LegacyMessagesFooter = MessagesFooter as unknown as React.ComponentType<LegacyComponentProps>;
const LegacyModalPortal = ModalPortal as unknown as React.ComponentType<LegacyComponentProps>;

function shouldShowJumpToNewestForLocation(location: { readonly isAtBottom?: boolean; readonly bottomOffset?: number } | null): boolean {
  if (!location) return false;
  if (location.isAtBottom) return false;
  const rawBottomOffset = location.bottomOffset;
  const bottomOffset = typeof rawBottomOffset === 'number' && Number.isFinite(rawBottomOffset) ? rawBottomOffset : 0;
  return bottomOffset > JUMP_TO_NEWEST_THRESHOLD;
}

const MessageFeedContent = ({
  currentChannel,
  currentChannelBreadcrumbs = EMPTY_CURRENT_CHANNEL_BREADCRUMBS,
  dropzoneHelpers,
  handleDropzoneStateChange,
  handleUploadCountChange,
  isSharedChannel = false,
  maxMediaAttachments,
  maxRichdownContentSize,
  bottomOverlayHeight = 0,
}: MessageFeedProps) => {
  const debugCountsResetRef = useRef(false);
  const isDebugEnabled = useMemo(() => isFeatureFlagEnabled('debug'), []);
  const exposeDebugSurface = useMemo(() => getMetaContent('build:environment') !== 'production' || isFeatureFlagEnabled('debug'), []);
  if (!debugCountsResetRef.current) {
    resetFeedDebugCounts();
    debugCountsResetRef.current = true;
  }
  const { setChannelComposerActive } = useRichdownComposerFocusRouter();
  const jumpHighlightTimeoutRef = useRef<number | null>(null);
  const jumpHighlightFrameRef = useRef<number | null>(null);
  const [jumpHighlight, setJumpHighlight] = useState<{ key: string; timestamp: number } | null>(null);
  const clearJumpHighlightSchedule = useCallback(() => {
    if (jumpHighlightTimeoutRef.current !== null) {
      window.clearTimeout(jumpHighlightTimeoutRef.current);
      jumpHighlightTimeoutRef.current = null;
    }
    if (jumpHighlightFrameRef.current !== null) {
      window.cancelAnimationFrame(jumpHighlightFrameRef.current);
      jumpHighlightFrameRef.current = null;
    }
  }, []);
  const handleMessageJumpComplete = useCallback((sortOrder: SortOrder) => {
    const key = String(sortOrder);
    const timestamp = Date.now();

    clearJumpHighlightSchedule();
    setJumpHighlight(null);
    jumpHighlightFrameRef.current = window.requestAnimationFrame(() => {
      jumpHighlightFrameRef.current = null;
      setJumpHighlight({ key, timestamp });
      jumpHighlightTimeoutRef.current = window.setTimeout(() => {
        jumpHighlightTimeoutRef.current = null;
        setJumpHighlight((current) => (current?.timestamp === timestamp ? null : current));
      }, JUMP_HIGHLIGHT_DURATION_MS);
    });
  }, [clearJumpHighlightSchedule]);
  const handleFeedMessageJumpComplete = useCallback((input: FeedMessageJumpInput) => {
    handleMessageJumpComplete(input.sortOrder);
  }, [handleMessageJumpComplete]);
  const { controller, engine, entitySnapshotMaps, profileId, session } = useFeedSessionLifecycle(
    currentChannel,
    handleMessageJumpComplete,
    isSharedChannel
  );
  const messageListItemCommitObserver = exposeDebugSurface ? incrementMessageListItemCommitCount : undefined;
  const rendererRowBuildObserver = exposeDebugSurface ? incrementRendererRowBuildCount : undefined;
  const rowCacheRef = useRef<{
    readonly onBuild: typeof rendererRowBuildObserver;
    readonly snapshots: EntitySnapshotMaps | null | undefined;
    readonly cache: ReturnType<typeof createRendererRowCache>;
  } | null>(null);
  if (
    !rowCacheRef.current ||
    rowCacheRef.current.snapshots !== entitySnapshotMaps ||
    rowCacheRef.current.onBuild !== rendererRowBuildObserver
  ) {
    if (exposeDebugSurface) rendererRowCacheCreationCount += 1;
    rowCacheRef.current = {
      onBuild: rendererRowBuildObserver,
      snapshots: entitySnapshotMaps,
      cache: createRendererRowCache(entitySnapshotMaps, rendererRowBuildObserver)
    };
  }
  const rowCache = rowCacheRef.current.cache;
  const feedScrollLocked = useAnyKeychainsRegistered(FEED_SCROLL_LOCK_KEYCHAINS);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const footerContainerRef = useRef<HTMLDivElement | null>(null);
  const readCursorControllerRef = useRef<FeedReadCursorController | null>(null);
  const replyResolverRef = useRef<EchoReplyResolver | null>(null);
  // Every surface launched from a rendered row keeps the row itself, not just its id:
  // virtualization can unmount the source row while the surface is open, and feed-only
  // messages have no legacy `messages` duplicate to re-fetch. See messageTargets.
  const { targets, openTarget, closeTarget, closeAllTargets, closeTargetsForMessage } = useMessageTargets();
  const retainedDeleteMessage = useRetainedModalValue(targets.delete);
  const retainedFullMessage = useRetainedModalValue(targets.fullMessage);
  const retainedConferenceTranscript = useRetainedModalValue(targets.conferenceTranscript);
  // The lightbox stays outside the registry: it is keyed by attachment payload and also
  // matches on sort order, so it has no single message id to close by.
  const [imageLightboxPayload, setImageLightboxPayload] = useState<Record<string, unknown> | null>(null);
  const [imageRemixTarget, setImageRemixTarget] = useState<{
    attachment: Record<string, unknown> & { id: string };
    channelId: string;
    workspaceId: string;
    sessionKey: string;
  } | null>(null);
  const retainedImageRemixTarget = useRetainedModalValue(imageRemixTarget);
  const imageRemixBusyRef = useRef(false);
  const imageRemixSessionRef = useRef<string | null>(null);
  const normalizedBottomOverlayHeight = useMemo(
    () => Math.max(0, Number(bottomOverlayHeight) || 0),
    [bottomOverlayHeight]
  );
  const composerOverlayStyle = useMemo<React.CSSProperties | undefined>(
    () => (normalizedBottomOverlayHeight > 0 ? { bottom: `${normalizedBottomOverlayHeight}px` } : undefined),
    [normalizedBottomOverlayHeight]
  );
  const renderWindow = useEngineRenderWindow(engine);
  const controllerSnapshot = useFeedControllerSnapshot(controller);
  const sessionSnapshot = useFeedSessionSnapshot(session);
  const workspaceId = currentChannel?.workspace_id ? String(currentChannel.workspace_id) : null;
  const activeChannelId = currentChannel?.id ? String(currentChannel.id) : null;
  const activeFeedFreshnessError =
    activeChannelId !== null && sessionSnapshot.conversationId === activeChannelId
      ? sessionSnapshot.freshnessError ?? null
      : null;
  // The feed reconciles its own channel from a durable cursor, so a terminal failure here is the
  // one "connected but behind" signal the catchup envelope ledger cannot see.
  useEffect(() => {
    reportFeedFreshness(activeFeedFreshnessError);
  }, [activeFeedFreshnessError]);
  // A separate teardown avoids a transient ready phase when one error changes into another.
  useEffect(() => () => reportFeedFreshness(null), []);
  const loadedItemSnapshotRef = useRef<LoadedItemSnapshot>(EMPTY_LOADED_ITEM_SNAPSHOT);
  if (loadedItemSnapshotRef.current.epoch !== controllerSnapshot.epoch) {
    loadedItemSnapshotRef.current = loadedItemSnapshot(controllerSnapshot.epoch, controllerSnapshot.items);
  }
  const loadedItems = loadedItemSnapshotRef.current;
  const olderPaginationPendingRef = useRef(false);
  olderPaginationPendingRef.current = controllerSnapshot.loading.older;
  if (!readCursorControllerRef.current) {
    readCursorControllerRef.current = new FeedReadCursorController();
  }
  if (!replyResolverRef.current) {
    replyResolverRef.current = new EchoReplyResolver();
  }

  const [scrollState, setScrollState] = useState<ScrollState>(() => engine.getScrollState());
  const isInteractive = Boolean(currentChannel && profileId);
  // useUnreadCounts caches its snapshot per ids-array IDENTITY; an inline array would
  // rebuild the snapshot (new object) every render and churn the row context through it.
  const unreadChannelIds = useMemo(() => [currentChannel?.id], [currentChannel?.id]);
  const unreadCountData = useUnreadCounts(unreadChannelIds);
  const unreadDividerRef = useRef<{ channelId: string | null; sortOrder: string | null }>({
    channelId: null,
    sortOrder: null
  });
  const [unreadDividerSortOrder, setUnreadDividerSortOrder] = useState<string | null>(null);
  const displayUnreadDividerSortOrder = useMemo(
    () => unreadDividerSortOrderForLoadedItems(loadedItems.items, unreadDividerSortOrder, profileId),
    [loadedItems.items, profileId, unreadDividerSortOrder]
  );
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
    []
  );
  // 2d: jumps currently center the target without a transient row highlight.
  const buildViewportInsets = useCallback(
    (composerHeightPx: number): ViewportInsets => ({
      composerOverlayPx: composerHeightPx + COMPOSER_GAP_PX,
      safeAreaBottomPx: normalizedBottomOverlayHeight
    }),
    [normalizedBottomOverlayHeight]
  );
  const viewportInsets = useMemo<ViewportInsets>(
    () => buildViewportInsets(COMPOSER_DEFAULT_PX),
    [buildViewportInsets]
  );
  const handleFooterResize = useCallback<ResizeObserverCallback>(
    (entries) => {
      const height = Math.max(0, entries[0]?.contentRect.height ?? 0);
      engine.setViewportInsets(buildViewportInsets(height));
    },
    [buildViewportInsets, engine]
  );

  useLayoutEffect(() => {
    if (!scrollerRef.current || !canvasRef.current) return undefined;

    engine.mount(scrollerRef.current, canvasRef.current);
    return () => {
      engine.unmount();
    };
  }, [engine]);

  useLayoutEffect(() => {
    if (!feedScrollLocked) return undefined;
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const previousOverflowY = scroller.style.overflowY;
    const previousTouchAction = scroller.style.touchAction;
    // Sanctioned external scroller style write: KeyMaster popovers must freeze feed scroll.
    scroller.style.overflowY = 'hidden';
    scroller.style.touchAction = 'none';
    return () => {
      scroller.style.overflowY = previousOverflowY;
      scroller.style.touchAction = previousTouchAction;
    };
  }, [feedScrollLocked]);

  useEffect(() => {
    return engine.subscribeScrollState((state) => {
      setScrollState(state);
    });
  }, [engine]);

  useEffect(() => {
    return clearJumpHighlightSchedule;
  }, [clearJumpHighlightSchedule]);

  useLayoutEffect(() => {
    if (jumpHighlight) markFeedJumpComplete();
  }, [jumpHighlight]);

  useEffect(() => {
    if (!exposeDebugSurface) return undefined;

    const debugSurface: MessageFeedDebugSurface = Object.freeze({
      engineState: () => {
        const currentScrollState = engine.getScrollState();
        const currentControllerSnapshot = controller.snapshot();
        return {
          atBottom: currentScrollState.atBottom,
          edges: currentControllerSnapshot.edgeState,
          epoch: engine.getWindow().epoch,
          loadedCount: currentControllerSnapshot.items.length,
          mode: currentScrollState.mode
        };
      },
      controllerSnapshot: () => {
        const currentControllerSnapshot = controller.snapshot();
        return {
          edges: currentControllerSnapshot.edgeState,
          itemCount: currentControllerSnapshot.items.length,
          loading: currentControllerSnapshot.loading,
          newerCursor: currentControllerSnapshot.newerCursor ?? null,
          olderCursor: currentControllerSnapshot.olderCursor ?? null,
          syncCursor: currentControllerSnapshot.syncCursor ?? null,
          windowEpoch: currentControllerSnapshot.windowEpoch
        };
      },
      renderCounts: itemShellRenderCountsSnapshot,
      workCounts: messageFeedWorkCountsSnapshot
    });

    window.__messageFeed = debugSurface;
    return () => {
      if (window.__messageFeed === debugSurface) delete window.__messageFeed;
    };
  }, [controller, engine, exposeDebugSurface]);

  useLayoutEffect(() => {
    engine.setViewportInsets(viewportInsets);
  }, [engine, viewportInsets]);

  useLayoutEffect(() => {
    const container = footerContainerRef.current;
    if (!container) return undefined;

    // Measure synchronously before first paint: ResizeObserver delivery is async, and a
    // boot frame using COMPOSER_DEFAULT_PX places the bottom edge zone under a taller
    // real composer (loading indicator flush against/behind the composer).
    engine.setViewportInsets(buildViewportInsets(container.getBoundingClientRect().height));

    const resizeObserver = new ResizeObserver(handleFooterResize);

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [buildViewportInsets, handleFooterResize, engine]);

  useEffect(() => {
    const activeChannelId = currentChannel?.id ? String(currentChannel.id) : null;
    if (!activeChannelId) return;

    if (unreadDividerRef.current.channelId !== activeChannelId) {
      unreadDividerRef.current = { channelId: activeChannelId, sortOrder: null };
      setUnreadDividerSortOrder(null);
    }

    if (unreadDividerRef.current.sortOrder != null) return;

    // Latch from the visit-entry snapshot, not live counts: entering a channel marks
    // it read immediately (overlay recompute), so live counts are already zero by the
    // time this runs. The snapshot records where the marker sat and whether anything
    // was unread at the moment the viewer clicked in, which also avoids latching on
    // marker updates with nothing unread (e.g. our own read cursor after a send).
    const unreadCountRecord = isRecord(unreadCountData) ? unreadCountData : null;
    const entryDividers = isRecord(unreadCountRecord?.entryDividers) ? unreadCountRecord.entryDividers : null;
    const entry = isRecord(entryDividers?.[activeChannelId]) ? entryDividers[activeChannelId] : null;
    if (!entry || entry.hasUnread !== true || entry.sortOrder == null) return;

    const markerSortOrder = String(entry.sortOrder);
    unreadDividerRef.current = { channelId: activeChannelId, sortOrder: markerSortOrder };
    setUnreadDividerSortOrder(markerSortOrder);
  }, [currentChannel?.id, unreadCountData]);

  useEffect(() => {
    const controllerRef = readCursorControllerRef.current;
    return () => {
      controllerRef?.dispose();
    };
  }, []);

  const readCursorContext = useMemo<ReadCursorContext>(
    () => ({
      channelId: activeChannelId,
      controllerSnapshot,
      items: renderWindow.items,
      profileId,
      scrollState,
      workspaceId
    }),
    [activeChannelId, controllerSnapshot, profileId, renderWindow.items, scrollState, workspaceId]
  );
  const readCursorContextRef = useRef<ReadCursorContext>(readCursorContext);

  useEffect(() => {
    readCursorContextRef.current = readCursorContext;
  }, [readCursorContext]);

  useEffect(() => {
    readCursorControllerRef.current?.handleChannelOpen(readCursorContext);
  }, [readCursorContext]);

  useEffect(() => {
    readCursorControllerRef.current?.handleBottomReach(readCursorContext);
  }, [readCursorContext]);

  useWindowEvent('focus', () => {
    readCursorControllerRef.current?.handleWindowEvent(readCursorContextRef.current, 'focus');
  });

  useWindowEvent('blur', () => {
    readCursorControllerRef.current?.handleWindowEvent(readCursorContextRef.current, 'blur');
  });

  useWindowEvent(MESSAGE_JUMP_REQUESTED_EVENT, (event) => {
    if (!session || !activeChannelId) return;
    handleFeedMessageJumpRequest(session, activeChannelId, customEventDetail(event), consumeMessageJump, {
      onJumpComplete: handleFeedMessageJumpComplete
    });
  });

  useEffect(() => {
    if (!session || !activeChannelId) return;

    void replyResolverRef.current?.resolveVisibleItems({
      channelId: activeChannelId,
      entitySnapshots: entitySnapshotMaps,
      getLoadedItem: (id) => session.getLoadedItem(id),
      items: renderWindow.items,
      markFailed: (rowId) => applyReplyResolutionPatch(session, rowId, {
        replyToMessage: null,
        replyToStatus: 'failed'
      }),
      updateResolved: (rowId, reply) => applyReplyResolutionPatch(session, rowId, {
        replyToMessage: reply,
        replyToStatus: null
      })
    });
  }, [activeChannelId, entitySnapshotMaps, renderWindow.items, session]);

  const clearUnreadDivider = useCallback(() => {
    unreadDividerRef.current = { channelId: currentChannel?.id ? String(currentChannel.id) : null, sortOrder: 'CLEARED' };
    setUnreadDividerSortOrder(null);
  }, [currentChannel?.id]);

  useWindowEvent('output:edit-message', (event) => {
    const detail = customEventDetail(event);
    if (!isRecord(detail) || !detail.messageId) return;

    const activeChannelId = currentChannel?.id != null ? String(currentChannel.id) : null;
    const eventChannelId = detail.channelId != null ? String(detail.channelId) : null;
    if (activeChannelId && eventChannelId && eventChannelId !== activeChannelId) return;

    openTarget('editing', detail.messageId, isRecord(detail.message) ? detail.message : null);
  });

  useWindowEvent('output:internal:edit-recent-message', async (event) => {
    const detail = customEventDetail(event);
    if (!isRecord(detail)) return;
    const editorId = typeof detail.editorId === 'string' ? detail.editorId : null;
    const editorChannelId = channelIdFromEditorId(editorId);
    if (!editorChannelId) return;

    const activeChannelId = currentChannel?.id != null ? String(currentChannel.id) : null;
    if (activeChannelId && editorChannelId !== activeChannelId) return;

    // The session window only knows the viewer's newest own message when it reaches
    // the live tail; a window detached by an around-jump or deep history scroll could
    // offer an older own message and silently edit the wrong one, so fall through to
    // the durable newest-first lookup instead.
    const snapshot = controller.snapshot();
    if (!shouldFetchLatestForJump(snapshot.edgeState.bottom, snapshot.newerCursor)) {
      const sessionRow = recentEditableOwnRendererRow(
        loadedItemSnapshotRef.current.items,
        rowCacheRef.current?.cache.rowFor ?? (() => undefined),
        profileId
      );
      if (sessionRow?.id != null) {
        openTarget('editing', sessionRow.id, sessionRow);
        return;
      }
    }

    try {
      const recentMessage = await findRecentEditableOwnMessage(editorChannelId);
      if (recentMessage) openTarget('editing', recentMessage.id, recentMessage);
    } catch {
      // Recent editable lookup is best-effort; stale local cache failures should not block composer focus.
    }
  });

  useWindowEvent('output:internal:messages:delete-message-modal', (event) => {
    const detail = customEventDetail(event);
    if (!isRecord(detail) || !detail.messageId) return;
    openTarget('delete', detail.messageId, isRecord(detail.message) ? detail.message : null);
  });

  useWindowEvent('output:internal:messages:conference-transcript-modal', (event) => {
    const detail = customEventDetail(event);
    if (!isRecord(detail) || !isRecord(detail.message)) return;
    openTarget('conferenceTranscript', detail.message.id, detail.message);
  });

  useWindowEvent('output:internal:messages:image-remix-modal', (event) => {
    const detail = customEventDetail(event);
    if (!isRecord(detail) || !isRecord(detail.attachment)) return;
    const eventChannelId = detail.channelId != null ? String(detail.channelId) : null;
    if (
      !workspaceId || !activeChannelId || eventChannelId !== activeChannelId ||
      typeof detail.attachment.id !== 'string' || !detail.attachment.id ||
      !profileId || imageRemixBusyRef.current
    ) return;

    setImageLightboxPayload(null);
    const sessionKey = crypto.randomUUID();
    imageRemixSessionRef.current = sessionKey;
    imageRemixBusyRef.current = false;
    setImageRemixTarget({
      attachment: { ...detail.attachment, id: detail.attachment.id },
      channelId: eventChannelId,
      workspaceId,
      sessionKey
    });
  });

  useWindowEvent(LIVE_MESSAGE_EVENT, (event) => {
    const detail = customEventDetail(event);
    if (!isRecord(detail) || detail.action !== 'destroy') return;
    const data = isRecord(detail.data) ? detail.data : null;
    if (!data) return;

    const activeChannelId = currentChannel?.id != null ? String(currentChannel.id) : null;
    const eventChannelId = data.channel_id != null ? String(data.channel_id) : null;
    if (activeChannelId && eventChannelId && eventChannelId !== activeChannelId) return;

    const deletedMessageId = data.id != null ? String(data.id) : null;
    if (deletedMessageId) closeTargetsForMessage(deletedMessageId);

    setImageLightboxPayload((currentPayload) => {
      if (!currentPayload) return currentPayload;

      const isSameMessageId = Boolean(currentPayload.messageId && data.id && currentPayload.messageId === data.id);
      const isSameSortOrder = Boolean(
        currentPayload.messageSortOrder &&
          (currentPayload.messageSortOrder === data.sort_order ||
            currentPayload.messageSortOrder === data.optimistic_sort_order)
      );

      return isSameMessageId || isSameSortOrder ? null : currentPayload;
    });
  });

  useEffect(() => {
    closeAllTargets();
    setImageLightboxPayload(null);
    setImageRemixTarget(null);
    imageRemixBusyRef.current = false;
    imageRemixSessionRef.current = null;
    clearJumpHighlightSchedule();
    setJumpHighlight(null);
  }, [clearJumpHighlightSchedule, closeAllTargets, currentChannel?.id]);

  const handleOpenFullMessageModal = useCallback(
    (message: unknown) => {
      if (!isRecord(message)) return;
      openTarget('fullMessage', message.id, message);
    },
    [openTarget]
  );

  const handleOpenImageLightbox = useCallback(
    (payload: unknown) => {
      if (!isRecord(payload)) return;
      setImageLightboxPayload({
        ...payload,
        channelId: payload.channelId || currentChannel?.id || null
      });
    },
    [currentChannel?.id]
  );

  const handleCloseImageLightbox = useCallback(() => {
    setImageLightboxPayload(null);
  }, []);

  const handleCloseImageRemixModal = useCallback(() => {
    if (imageRemixBusyRef.current) return;
    imageRemixSessionRef.current = null;
    setImageRemixTarget(null);
  }, []);

  const handleImageRemixBusyChange = useCallback((busy: boolean, sessionKey: string) => {
    if (imageRemixSessionRef.current === sessionKey) imageRemixBusyRef.current = busy;
  }, []);

  const handleJumpToNewest = useCallback(() => {
    void session?.jumpToLatest().catch(ignoreMessageFeedError);
  }, [session]);

  const handleCancelEditing = useCallback(() => {
    closeTarget('editing');
  }, [closeTarget]);

  // A confirmed delete closes every surface this message opened, not just edit state —
  // the same rule the live-journal destroy event applies for deletes from other clients.
  const handleMessageDeleted = useCallback(
    (deletedMessageId: string) => {
      closeTargetsForMessage(deletedMessageId);
    },
    [closeTargetsForMessage]
  );

  const handleCloseDeleteMessageModal = useCallback(() => {
    closeTarget('delete');
  }, [closeTarget]);

  const handleCloseConferenceTranscriptModal = useCallback(() => {
    closeTarget('conferenceTranscript');
  }, [closeTarget]);

  const handleCloseFullMessageModal = useCallback(() => {
    closeTarget('fullMessage');
  }, [closeTarget]);

  const areDifferentDays = useCallback(
    (timestamp1: unknown, timestamp2: unknown) => areRendererRowsDifferentDays(timestamp1, timestamp2, dateFormatter),
    [dateFormatter]
  );
  const getIsOlderPaginationPending = useCallback(() => olderPaginationPendingRef.current, []);

  const messageListContext = useMemo(
    () =>
      buildMessageFeedItemContext({
        currentChannel,
        currentChannelBreadcrumbs,
        isInteractive,
        isDebugEnabled,
        isSharedChannel,
        handleOpenFullMessageModal,
        handleOpenImageLightbox,
        unreadDividerSortOrder: displayUnreadDividerSortOrder,
        clearUnreadDivider,
        profileId,
        getIsOlderPaginationPending
      }),
    [
      clearUnreadDivider,
      currentChannel,
      currentChannelBreadcrumbs,
      displayUnreadDividerSortOrder,
      getIsOlderPaginationPending,
      handleOpenFullMessageModal,
      handleOpenImageLightbox,
      isDebugEnabled,
      isInteractive,
      isSharedChannel,
      profileId
    ]
  );

  const registerTopEdge = useCallback(
    (el: HTMLDivElement | null) => {
      engine.registerEdgeElement('top', el);
    },
    [engine]
  );
  const registerBottomEdge = useCallback(
    (el: HTMLDivElement | null) => {
      engine.registerEdgeElement('bottom', el);
    },
    [engine]
  );

  const handleRetryTopEdge = useCallback(() => {
    void session?.loadOlder().catch(ignoreMessageFeedError);
  }, [session]);

  const handleRetryBottomEdge = useCallback(() => {
    if (!session) return;
    if (controllerSnapshot.newerCursor) {
      void session.loadNewer().catch(ignoreMessageFeedError);
      return;
    }
    void session.retryBootLatest().catch(ignoreMessageFeedError);
  }, [controllerSnapshot.newerCursor, session]);

  const renderFeedWindowItem = useCallback(
    (item: ChatItem) => {
      const itemIndex = loadedItems.indexesByKey.get(item.itemKey);
      const prevItem = itemIndex === undefined ? undefined : loadedItems.items[itemIndex - 1];
      const nextItem = itemIndex === undefined ? undefined : loadedItems.items[itemIndex + 1];
      const isPlaceholder = renderWindow.placeholderKeys.has(item.itemKey);
      const data = rowCache.rowFor(item);
      const prevData = rowCache.rowFor(prevItem);
      const nextData = rowCache.rowFor(nextItem);

      return (
        <ItemShell
          key={item.itemKey}
          areDifferentDays={areDifferentDays}
          context={messageListContext}
          data={data}
          engine={engine}
          isPlaceholder={isPlaceholder}
          item={item}
          jumpHighlightKey={jumpHighlight?.key ?? null}
          jumpHighlightTimestamp={jumpHighlight?.timestamp ?? null}
          nextData={nextData}
          onMessageListItemCommit={messageListItemCommitObserver}
          prevData={prevData}
        />
      );
    },
    [
      areDifferentDays,
      engine,
      jumpHighlight,
      loadedItems,
      messageListContext,
      messageListItemCommitObserver,
      renderWindow.placeholderKeys,
      rowCache
    ]
  );

  const latestJumpNeedsFetch = shouldFetchLatestForJump(controllerSnapshot.edgeState.bottom, controllerSnapshot.newerCursor);
  const jumpToNewestVisible = Boolean(
    session &&
      (sessionSnapshot.unseenPresentCount > 0 ||
        latestJumpNeedsFetch ||
        shouldShowJumpToNewestForLocation({
          isAtBottom: scrollState.atBottom && !latestJumpNeedsFetch,
          bottomOffset: scrollState.distanceFromBottom
        }))
  );
  const showBootSkeleton = Boolean(session && renderWindow.epoch === 0);

  return (
    <div
      className="dropzone-target relative mx-auto flex h-full flex-1 flex-col"
      onClick={setChannelComposerActive}
    >
      <div className="relative min-h-0 flex-1">
        <div className="relative min-h-0 h-full" data-feed-shell>
          <div
            className="scrollbar-hidden relative h-full overflow-y-auto overflow-x-hidden bg-transparent"
            data-feed-scroller
            id={ENGINE_SCROLLER_ID}
            ref={scrollerRef}
            style={SCROLLER_STYLE}
          >
            <div className="relative w-full" data-feed-canvas ref={canvasRef}>
              <FeedEdgeZone onRetry={handleRetryTopEdge} register={registerTopEdge} side="top" state={controllerSnapshot.edgeState.top} />
              {renderWindow.items.map(renderFeedWindowItem)}
              <FeedEdgeZone
                onRetry={handleRetryBottomEdge}
                register={registerBottomEdge}
                side="bottom"
                state={controllerSnapshot.edgeState.bottom}
              />
            </div>
          </div>
          <FeedScrollbar
            engine={engine}
            key={activeChannelId ?? 'no-channel'}
            scrollerId={ENGINE_SCROLLER_ID}
            scrollerRef={scrollerRef}
            scrollState={scrollState}
            viewportInsets={viewportInsets}
          />
          {showBootSkeleton ? <BootSkeleton /> : null}

          <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-(--stack-messages-sticky-header)" style={composerOverlayStyle}>
            <div className="scrollbar-gutter-stable pointer-events-none absolute -top-6 right-0 left-0 overflow-y-auto px-4">
              <div className="mx-auto flex w-full max-w-2xl justify-end">
                <NetworkStatus />
              </div>
            </div>

            <div className="scrollbar-gutter-stable pointer-events-none absolute -top-14 right-0 left-0 overflow-y-auto px-4">
              <div className="mx-auto flex w-full max-w-2xl justify-center">
                <JumpToNewest isVisible={jumpToNewestVisible} onJump={handleJumpToNewest} />
              </div>
            </div>

            <div ref={footerContainerRef} className="scrollbar-gutter-stable overflow-y-auto px-4" data-feed-composer>
              <div className="mx-auto w-full max-w-2xl">
                <LegacyMessagesFooter
                  isInteractive={isInteractive}
                  channelId={currentChannel?.id}
                  editingMessageId={targets.editing?.messageId ?? null}
                  editingMessageSnapshot={targets.editing?.message ?? null}
                  onCancelEditing={handleCancelEditing}
                  dropzoneHelpers={dropzoneHelpers}
                  onDropzoneStateChange={handleDropzoneStateChange}
                  onUploadCountChange={handleUploadCountChange}
                  maxMediaAttachments={maxMediaAttachments}
                  maxRichdownContentSize={maxRichdownContentSize}
                  currentChannel={currentChannel}
                  onOwnMessageSubmitted={clearUnreadDivider}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <LegacyModalPortal
        open={Boolean(targets.fullMessage)}
        onClose={handleCloseFullMessageModal}
        keychainId={KEYCHAIN.MODAL__FULL_MESSAGE}
        className="max-w-2xl"
        backdropClassName="bg-neutral-900/80 backdrop-blur-lg dark:bg-neutral-950/80"
      >
        {retainedFullMessage ? (
          <LegacyFullMessageModal message={retainedFullMessage.message} onClose={handleCloseFullMessageModal} />
        ) : null}
      </LegacyModalPortal>

      <LegacyModalPortal
        open={Boolean(targets.delete)}
        onClose={handleCloseDeleteMessageModal}
        size="form"
        keychainId={KEYCHAIN.MODAL__DELETE_MESSAGE}
      >
        {retainedDeleteMessage ? (
          <LegacyDeleteMessageConfirmation
            messageId={retainedDeleteMessage.messageId}
            message={retainedDeleteMessage.message}
            onClose={handleCloseDeleteMessageModal}
            onDeleted={handleMessageDeleted}
          />
        ) : null}
      </LegacyModalPortal>

      <LegacyModalPortal
        open={Boolean(targets.conferenceTranscript)}
        onClose={handleCloseConferenceTranscriptModal}
        size="browser"
        keychainId={KEYCHAIN.MODAL__CONFERENCE_TRANSCRIPT}
      >
        {retainedConferenceTranscript ? (
          <LegacyConferenceTranscriptModal
            conferenceTranscript={retainedConferenceTranscript.message}
            onClose={handleCloseConferenceTranscriptModal}
          />
        ) : null}
      </LegacyModalPortal>

      <LegacyImageAttachmentLightbox
        open={Boolean(imageLightboxPayload)}
        onClose={handleCloseImageLightbox}
        src={stringPayloadValue(imageLightboxPayload, 'src')}
        publicUrl={stringPayloadValue(imageLightboxPayload, 'publicUrl')}
        previewSrc={stringPayloadValue(imageLightboxPayload, 'previewSrc')}
        srcset={stringPayloadValue(imageLightboxPayload, 'srcset')}
        alt={stringPayloadValue(imageLightboxPayload, 'alt') ?? 'Image'}
        width={numberPayloadValue(imageLightboxPayload, 'width')}
        height={numberPayloadValue(imageLightboxPayload, 'height')}
        thumbBounds={imageLightboxPayload?.thumbBounds || null}
        slides={imageLightboxPayload?.slides || null}
        index={numberPayloadValue(imageLightboxPayload, 'index') ?? 0}
        channelId={stringPayloadValue(imageLightboxPayload, 'channelId')}
      />
      <LegacyModalPortal
        open={Boolean(imageRemixTarget)}
        onClose={handleCloseImageRemixModal}
        keychainId={KEYCHAIN.MODAL__IMAGE_REMIX}
        className="h-[90dvh] max-h-200 max-w-2xl"
      >
        {retainedImageRemixTarget ? (
          <ImageRemixModal
            key={retainedImageRemixTarget.sessionKey}
            sessionKey={retainedImageRemixTarget.sessionKey}
            active={Boolean(imageRemixTarget) && retainedImageRemixTarget.channelId === activeChannelId}
            sourceAttachment={retainedImageRemixTarget.attachment}
            channelId={retainedImageRemixTarget.channelId}
            workspaceId={retainedImageRemixTarget.workspaceId}
            onClose={handleCloseImageRemixModal}
            onBusyChange={handleImageRemixBusyChange}
          />
        ) : null}
      </LegacyModalPortal>
    </div>
  );
};

function MessageFeed(props: MessageFeedProps): React.ReactElement {
  return (
    <FeedErrorBoundary>
      <MessageFeedContent {...props} />
    </FeedErrorBoundary>
  );
}

function useEngineRenderWindow(engine: FeedEngine<ChatItem>): RenderWindow<ChatItem> {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      engine.subscribeWindow(() => {
        onStoreChange();
      }),
    [engine]
  );
  const getSnapshot = useCallback(() => engine.getWindow(), [engine]);
  const renderWindow = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [bridgedWindow, setBridgedWindow] = useState<RenderWindow<ChatItem>>(renderWindow);

  useLayoutEffect(() => {
    let initial = true;
    return engine.subscribeWindow((nextWindow) => {
      if (initial) {
        initial = false;
        setBridgedWindow(nextWindow);
        return;
      }
      flushSync(() => setBridgedWindow(nextWindow));
    });
  }, [engine]);

  return bridgedWindow;
}

function useFeedControllerSnapshot(controller: FeedController): FeedControllerSnapshot {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      controller.subscribe(() => {
        onStoreChange();
      }),
    [controller]
  );
  const getSnapshot = useCallback(() => controller.snapshot(), [controller]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function useFeedSessionSnapshot(session: FeedSession | null): FeedSessionSnapshot {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!session) return noopUnsubscribe;
      return session.subscribe(() => {
        onStoreChange();
      });
    },
    [session]
  );
  const getSnapshot = useCallback(() => session?.getSnapshot() ?? EMPTY_SESSION_SNAPSHOT, [session]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const ItemShell = memo(function ItemShell({
  areDifferentDays,
  context,
  data,
  engine,
  isPlaceholder,
  item,
  jumpHighlightKey,
  jumpHighlightTimestamp,
  nextData,
  onMessageListItemCommit,
  prevData
}: {
  readonly areDifferentDays: (timestamp1: unknown, timestamp2: unknown) => boolean;
  readonly context: unknown;
  readonly data: MessageListRendererRow | undefined;
  readonly engine: FeedEngine<ChatItem>;
  readonly isPlaceholder: boolean;
  readonly item: ChatItem;
  readonly jumpHighlightKey: string | null;
  readonly jumpHighlightTimestamp: number | null;
  readonly nextData: MessageListRendererRow | undefined;
  readonly onMessageListItemCommit: ((itemKey: string) => void) | undefined;
  readonly prevData: MessageListRendererRow | undefined;
}) {
  // Counted in an effect so only COMMITTED renders register: the flushSync window bridge
  // can interrupt a scheduled render pass, and discarded attempts would inflate a
  // render-body counter without any DOM work actually happening.
  useEffect(() => {
    incrementItemShellRenderCount(item.itemKey);
  });
  const registerItem = useCallback(
    (el: HTMLDivElement | null) => {
      engine.registerItemElement(item.itemKey, el);
    },
    [engine, item.itemKey]
  );
  const invalidMessage = isInvalidEchoMessage(item);
  const isJumpHighlighted = Boolean(jumpHighlightKey && item.sortOrder && String(item.sortOrder) === jumpHighlightKey);

  return (
    <div
      className={`absolute right-0 left-0 top-0${isJumpHighlighted ? ' feed-jump-highlight' : ''}`}
      data-item-key={item.itemKey}
      data-jump-highlight-timestamp={isJumpHighlighted ? jumpHighlightTimestamp ?? undefined : undefined}
      data-message-id={item.id}
      data-sort-order={item.sortOrder}
      ref={registerItem}
      style={ITEM_SHELL_STYLE}
    >
      {isPlaceholder ? (
        <FeedPlaceholderRow />
      ) : invalidMessage ? (
        <InvalidMessageRow />
      ) : (
        <LegacyMessageListItem
          debugItemKey={item.itemKey}
          data={data}
          context={context}
          nextData={nextData}
          onDebugCommit={onMessageListItemCommit}
          prevData={prevData}
          areDifferentDays={areDifferentDays}
        />
      )}
    </div>
  );
});

function customEventDetail(event: Event): unknown {
  return 'detail' in event ? event.detail : undefined;
}

/** Newest own editable message in the loaded window; shares the durable lookup's eligibility rule. */
function recentEditableOwnRendererRow(
  items: readonly ChatItem[],
  rowFor: (item: ChatItem | undefined) => MessageListRendererRow | undefined,
  profileId: string | null
): MessageListRendererRow | null {
  if (!profileId) return null;
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const row = rowFor(items[index]);
    if (!isRecord(row) || row.id == null) continue;
    if (!isEditableOwnMessageRow(row, profileId, 'profile')) continue;
    return row;
  }
  return null;
}

function loadedItemSnapshot(epoch: number, items: readonly ChatItem[]): LoadedItemSnapshot {
  const indexesByKey = new Map<string, number>();
  items.forEach((item, index) => {
    indexesByKey.set(item.itemKey, index);
  });
  return {
    epoch,
    indexesByKey,
    items
  };
}

function incrementItemShellRenderCount(itemKey: string): void {
  itemShellRenderCounts.set(itemKey, (itemShellRenderCounts.get(itemKey) ?? 0) + 1);
}

function incrementMessageListItemCommitCount(itemKey: string): void {
  messageListItemCommitCounts.set(itemKey, (messageListItemCommitCounts.get(itemKey) ?? 0) + 1);
}

function incrementRendererRowBuildCount(item: ChatItem): void {
  rendererRowBuildCounts.set(item.itemKey, (rendererRowBuildCounts.get(item.itemKey) ?? 0) + 1);
}

function resetFeedDebugCounts(): void {
  itemShellRenderCounts.clear();
  messageListItemCommitCounts.clear();
  rendererRowBuildCounts.clear();
  rendererRowCacheCreationCount = 0;
}

function itemShellRenderCountsSnapshot(): Record<string, number> {
  return Object.fromEntries(itemShellRenderCounts.entries());
}

function messageFeedWorkCountsSnapshot(): MessageFeedWorkCounts {
  return {
    messageListItemCommits: Object.fromEntries(messageListItemCommitCounts.entries()),
    rendererRowBuilds: Object.fromEntries(rendererRowBuildCounts.entries()),
    rendererRowCacheCreations: rendererRowCacheCreationCount
  };
}

function stringPayloadValue(payload: Record<string, unknown> | null, key: string): string | null {
  const value = payload?.[key];
  return typeof value === 'string' ? value : null;
}

function numberPayloadValue(payload: Record<string, unknown> | null, key: string): number | undefined {
  const value = payload?.[key];
  return typeof value === 'number' ? value : undefined;
}

function applyReplyResolutionPatch(
  session: FeedSession,
  rowId: MessageId,
  patch: {
    readonly replyToMessage: ReplyParentRow | null;
    readonly replyToStatus: 'failed' | null;
  }
): boolean {
  const item = session.getLoadedItem(rowId);
  if (!item) return false;

  const rendererRow = isRecord(item.rendererRow) ? item.rendererRow : {};
  return session.applyLoadedRendererRowUpdate(rowId, {
    ...rendererRow,
    ...patch
  });
}

function ignoreMessageFeedError(_error: unknown): void {}

function noopUnsubscribe(): void {}

export default MessageFeed;
